import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import ReaderImage from "@/pages/read/components/ReaderImage";
import ReaderSettings from "@/pages/read/components/ReaderSettings";
import usePageMeta, { SITE_NAME } from "@/hooks/usePageMeta";
import { buildChapters, getComicById } from "@/utils/manga";
import { getChapterPages } from "@/utils/reader";
import {
  getReaderSettings,
  saveReaderSettings,
  setProgress,
  type ReaderSettings as ReaderSettingsType,
} from "@/utils/storage";

export default function ReadPage() {
  const { comicId, chapterId } = useParams();
  const navigate = useNavigate();
  const comic = comicId ? getComicById(comicId) : undefined;
  const chapters = comic ? buildChapters(comic) : [];
  const currentIndex = chapters.findIndex((item) => item.id === chapterId);
  const isValid = Boolean(comic) && currentIndex >= 0;

  const [settings, setSettings] = useState<ReaderSettingsType>(getReaderSettings);
  const [loadedCount, setLoadedCount] = useState(1);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const currentChapter = currentIndex >= 0 ? chapters[currentIndex] : undefined;

  usePageMeta(
    comic && currentChapter
      ? `${comic.title} ${currentChapter.title} - ${SITE_NAME}`
      : `阅读 - ${SITE_NAME}`,
    comic ? `${comic.title} ${currentChapter ? currentChapter.title : ""} 无删减免费下拉在线阅读。` : "开始阅读韩漫。",
  );

  useEffect(() => {
    setLoadedCount(1);
    window.scrollTo({ top: 0 });
  }, [chapterId]);

  useEffect(() => {
    if (comic && chapterId) {
      setProgress(comic.id, chapterId);
    }
  }, [comic, chapterId]);

  const maxLoaded = currentIndex >= 0 ? chapters.length - currentIndex : 0;

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setLoadedCount((prev) => (prev < maxLoaded ? prev + 1 : prev));
        }
      },
      { rootMargin: "300px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [maxLoaded]);

  const handleSettings = (next: ReaderSettingsType) => {
    setSettings(next);
    saveReaderSettings(next);
  };

  if (!isValid || !comic || !currentChapter) {
    return (
      <SiteLayout>
        <div className="w-full px-4 md:px-8 py-24 flex flex-col items-center text-center">
          <span className="w-20 h-20 flex items-center justify-center rounded-full bg-background-200 text-foreground-400 text-4xl mb-5">
            <i className="ri-file-warning-line"></i>
          </span>
          <h1 className="font-heading font-bold text-2xl text-foreground-950">
            章节不存在
          </h1>
          <p className="text-sm text-foreground-500 mt-3 max-w-md">
            这部漫画或章节可能已被移除，返回首页继续浏览其他作品吧。
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-home-5-line"></i>
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const prevChapter = currentIndex > 0 ? chapters[currentIndex - 1] : undefined;
  const nextChapter =
    currentIndex < chapters.length - 1 ? chapters[currentIndex + 1] : undefined;
  const loadedChapters = chapters.slice(currentIndex, currentIndex + loadedCount);

  const themeClass =
    settings.theme === "dark"
      ? "bg-foreground-950 text-background-50"
      : settings.theme === "green"
        ? "bg-secondary-50 text-foreground-900"
        : "bg-background-50 text-foreground-950";
  const fontClass =
    settings.fontSize === "sm"
      ? "text-sm"
      : settings.fontSize === "lg"
        ? "text-lg"
        : "text-base";

  return (
    <SiteLayout>
      <div className={`w-full transition-colors ${themeClass}`}>
        <div className="w-full px-4 md:px-8 pt-5">
          <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 flex-wrap mb-4">
            <Link to="/" className="hover:text-accent-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={`/detail/${comic.id}`} className="hover:text-accent-600">
              {comic.title}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-950">{currentChapter.title}</span>
          </nav>
        </div>

        <div className="sticky top-16 z-40 w-full px-4 md:px-8">
          <div className={`relative w-full flex items-center gap-2 md:gap-3 px-3 md:px-4 py-2.5 rounded-lg border border-background-200 bg-background-50/95 backdrop-blur-md`}>
            <Link
              to={`/detail/${comic.id}`}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-xs md:text-sm text-foreground-700 hover:bg-background-200 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              <span className="hidden sm:inline">返回目录</span>
            </Link>

            <div className="hidden md:flex flex-col leading-tight min-w-0 flex-1">
              <span className="text-sm font-medium text-foreground-950 truncate">
                {comic.title}
              </span>
              <span className="text-xs text-foreground-500">
                {currentChapter.title}
              </span>
            </div>

            <select
              value={currentChapter.id}
              onChange={(event) =>
                navigate(`/read/${comic.id}/${event.target.value}`)
              }
              className="ml-auto h-9 max-w-[140px] md:max-w-[180px] px-2 rounded-md border border-background-300 bg-background-100 text-xs md:text-sm text-foreground-900 outline-none cursor-pointer"
              aria-label="跳转章节"
            >
              {chapters.map((chapter) => (
                <option key={chapter.id} value={chapter.id}>
                  {chapter.title}
                </option>
              ))}
            </select>

            <button
              type="button"
              onClick={() => setSettingsOpen((prev) => !prev)}
              className="relative inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-xs md:text-sm text-foreground-700 hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-settings-3-line"></i>
              <span className="hidden sm:inline">阅读设置</span>
            </button>

            {settingsOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setSettingsOpen(false)}
                ></div>
                <ReaderSettings settings={settings} onChange={handleSettings} />
              </>
            )}
          </div>
        </div>

        <div className="w-full px-4 md:px-8 py-6">
          <div className="mx-auto w-full max-w-[720px]">
            {loadedChapters.map((chapter) => (
              <div key={chapter.id} className="mb-2">
                <div className="flex items-center gap-3 py-5">
                  <span className="flex-1 h-px bg-background-300"></span>
                  <span className={`font-heading font-semibold ${fontClass} text-foreground-700 whitespace-nowrap`}>
                    {comic.title} · {chapter.title}
                  </span>
                  <span className="flex-1 h-px bg-background-300"></span>
                </div>
                <div className="flex flex-col gap-1 rounded-lg overflow-hidden border border-background-200">
                  {getChapterPages(chapter.index).map((src, index) => (
                    <ReaderImage
                      key={`${chapter.id}-${index}`}
                      src={src}
                      alt={`${comic.title} ${chapter.title} 第${index + 1}页 韩漫在线`}
                    />
                  ))}
                </div>
              </div>
            ))}

            <div ref={sentinelRef} className="h-6" />

            {loadedCount < maxLoaded ? (
              <p className="text-center text-sm text-foreground-400 py-6">
                继续向下滑动，自动加载下一话…
              </p>
            ) : (
              <div className="py-8 flex flex-col items-center gap-4">
                <p className="text-sm text-foreground-500">
                  {nextChapter ? "本话已读完" : "已经是最后一话了"}
                </p>
                <div className="flex flex-wrap items-center justify-center gap-3">
                  {prevChapter ? (
                    <Link
                      to={`/read/${comic.id}/${prevChapter.id}`}
                      className="inline-flex items-center gap-2 h-11 px-5 rounded-md border border-background-300 text-sm text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-arrow-left-line"></i>
                      上一话
                    </Link>
                  ) : (
                    <span className="inline-flex items-center gap-2 h-11 px-5 rounded-md border border-background-200 text-sm text-foreground-300 whitespace-nowrap">
                      <i className="ri-arrow-left-line"></i>
                      已是第一话
                    </span>
                  )}
                  {nextChapter ? (
                    <Link
                      to={`/read/${comic.id}/${nextChapter.id}`}
                      className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent-500 text-white text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap"
                    >
                      下一话
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  ) : (
                    <Link
                      to={`/detail/${comic.id}`}
                      className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                    >
                      返回目录
                      <i className="ri-list-check"></i>
                    </Link>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}